import React, { useEffect, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import {
  Col, Divider, Flex, Progress, Row, Tag, Typography,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AlumlyDataGrid from '../../../components/AlumlyDataGrid';
import StatusBadge from '../../../components/Badges/StatusBadge';
import HeaderLabel from '../../../components/Form/HeaderLabel';
import AlumlyBreadCrumb, { PathT } from '../../../components/AlumlyBreadCrumb';
import AlumlyButton from '../../../components/AlumlyButton';
import { PermissionEnum } from '../../../enums/permission';
import { RouteNameEnum } from '../../../enums/routeName';
import { ServiceNameEnum } from '../../../enums/serviceName';
import usePermission from '../../../hooks/usePermission';
import useServices from '../../../hooks/useServices';
import { AxiosService } from '../../../utils/axiosService';
import { DateTimeUtils } from '../../../utils/dateTime';
import { PathBuilder } from '../../../utils/pathBuilder';
import DeleteEventModal from '../components/DeleteEventModal';

const { Title, Text } = Typography;

const EventDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { checkPermission } = usePermission();
  const [eventDetail, setEventDetail] = useState<any>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [regCount, setRegCount] = useState(0);

  const { service: getEventDetailService } = useServices(ServiceNameEnum.event.detail);
  const { service: getRegistrationsService } = useServices(ServiceNameEnum.event.registrations);

  const getEventDetail = async (): Promise<void> => {
    const params = { id };
    const { data, error } = await getEventDetailService.get({ params });
    if (data) {
      // Resolve coverFile presignUrl
      if (data.coverFile) {
        data.coverImageUrl = await data.coverFile.presignUrl || data.coverFile.presignUrl;
      }
      setEventDetail(data);
    } else if (error) {
      const errorResponse = AxiosService.getErrorResponse(error);
      toast.error(errorResponse?.message, { toastId: 'allToast' });
      navigate(RouteNameEnum.event.index);
    }
  };

  const navigateToEdit = (): void => {
    const params = { id };
    const editPath = PathBuilder.buildPath(RouteNameEnum.event.edit, { params });
    navigate(editPath);
  };

  const getRegistrations = async (): Promise<void> => {
    const params = { id };
    const { data, error } = await getRegistrationsService.get({ params });
    if (data) {
      setRegistrations(data.items || []);
      setRegCount(data.count || 0);
    }
  };

  const exportCsv = (): void => {
    const headers = ['First Name', 'Last Name', 'Email', 'Phone', 'Faculty', 'SHI', 'Student ID', 'Registered At'];
    const rows = registrations.map((r) => [
      r.firstName,
      r.lastName,
      r.email,
      r.mobileNumber,
      r.faculty,
      r.graduationYear,
      r.studentId,
      DateTimeUtils.dateTimeFormat(r.registeredAt),
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell: any) => `"${(cell || '-').toString().replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([`﻿${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const eventTitle = eventDetail?.titleML?.en || 'event';
    link.download = `registrations-${eventTitle.replace(/[^a-zA-Z0-9]/g, '_')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    getEventDetail();
    getRegistrations();
  }, []);

  const path: PathT[] = [
    { label: 'Events', value: RouteNameEnum.event.index },
    { label: 'Event detail' },
  ];

  const getML = (ml: any, key: string): string => ml?.[key] || '-';

  return (
    <div>
      <AlumlyBreadCrumb path={path} />
      <Title level={4} style={{ fontWeight: 'bold' }}>Event detail</Title>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Flex gap={16} align="center">
          <Text type="secondary">status</Text>
          <StatusBadge status={!!eventDetail?.isPublished} />
        </Flex>
        <Flex gap={16}>
          {checkPermission([PermissionEnum.code.eventDelete]) && (
            <AlumlyButton label="rxvWhite" icon={<DeleteIcon />} width={200} onClick={() => setOpenDeleteModal(true)}>
              Delete event
            </AlumlyButton>
          )}
          {checkPermission([PermissionEnum.code.eventEdit]) && (
            <AlumlyButton label="rxv" icon={<EditIcon />} width={200} onClick={navigateToEdit}>
              Edit event
            </AlumlyButton>
          )}
        </Flex>
      </div>

      <DeleteEventModal
        eventId={Number(id)}
        isOpen={openDeleteModal}
        handleClose={() => setOpenDeleteModal(false)}
      />

      {/* General Info */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label="General info" />
        <div style={{ backgroundColor: 'white' }}>
          {eventDetail?.coverFile?.presignUrl && (
            <>
              <div style={{ paddingLeft: 16, paddingTop: 16 }}>
                <Text type="secondary">Cover Image</Text>
                <img
                  src={eventDetail.coverFile.presignUrl}
                  alt=""
                  style={{
                    display: 'block', width: 300, height: 200, marginTop: 8, borderRadius: 4, objectFit: 'cover',
                  }}
                />
              </div>
              <Divider style={{ marginTop: 16, marginBottom: 16 }} />
            </>
          )}
          <div style={{ paddingLeft: 16, marginTop: 16 }}>
            <Text type="secondary">Title (EN)</Text>
            <div>{getML(eventDetail?.titleML, 'en')}</div>
          </div>
          <Divider style={{ marginTop: 16, marginBottom: 16 }} />
          <div style={{ paddingLeft: 16 }}>
            <Text type="secondary">Title (TH)</Text>
            <div>{getML(eventDetail?.titleML, 'th')}</div>
          </div>
          <Divider style={{ marginTop: 16, marginBottom: 16 }} />
          <div style={{ paddingLeft: 16 }}>
            <Text type="secondary">Category</Text>
            <div>
              {eventDetail?.category
                ? <Tag>{eventDetail.category}</Tag>
                : '-'}
            </div>
          </div>
          <Divider style={{ marginTop: 16, marginBottom: 16 }} />
          <div style={{ paddingLeft: 16, display: 'flex', gap: 64 }}>
            <div>
              <Text type="secondary">Start</Text>
              <div>{DateTimeUtils.dateTimeFormat(eventDetail?.startAt) || '-'}</div>
            </div>
            <div>
              <Text type="secondary">End</Text>
              <div>{DateTimeUtils.dateTimeFormat(eventDetail?.endAt) || '-'}</div>
            </div>
          </div>
          <Divider style={{ marginTop: 16, marginBottom: 16 }} />
          <div style={{ paddingLeft: 16 }}>
            <Text type="secondary">Format</Text>
            <div>{getML(eventDetail?.formatML, 'en')}</div>
          </div>
          <Divider style={{ marginTop: 16, marginBottom: 16 }} />
          <div style={{ paddingLeft: 16 }}>
            <Text type="secondary">Tags</Text>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              {Array.isArray(eventDetail?.tags) && eventDetail.tags.length > 0
                ? eventDetail.tags.map((tag: string) => <Tag key={tag}>{tag}</Tag>)
                : <div>-</div>}
            </div>
          </div>
          <Divider style={{ marginTop: 16, marginBottom: 16 }} />
        </div>
      </div>

      {/* Gallery */}
      {eventDetail?.galleryImageUrls && eventDetail.galleryImageUrls.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <HeaderLabel label="Gallery Images" />
          <Row gutter={[16, 16]} style={{ padding: 16, backgroundColor: 'white' }}>
            {eventDetail.galleryImageUrls.map((url: string, idx: number) => (
              <Col key={`gallery-${idx}`}>
                <img
                  style={{
                    width: 210, height: 150, borderRadius: 4, objectFit: 'cover', display: 'block',
                  }}
                  src={url}
                  alt=""
                />
              </Col>
            ))}
          </Row>
        </div>
      )}

      {/* Description */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label="Description" />
        <div style={{ backgroundColor: 'white', padding: 16 }}>
          <Row gutter={[32, 32]}>
            <Col xs={24} md={12}>
              <Text type="secondary">Description (EN)</Text>
              {eventDetail?.descriptionML?.en
                ? <div dangerouslySetInnerHTML={{ __html: eventDetail.descriptionML.en }} />
                : <div>-</div>}
            </Col>
            <Col xs={24} md={12}>
              <Text type="secondary">Description (TH)</Text>
              {eventDetail?.descriptionML?.th
                ? <div dangerouslySetInnerHTML={{ __html: eventDetail.descriptionML.th }} />
                : <div>-</div>}
            </Col>
          </Row>
        </div>
      </div>

      {/* Location */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label="Location" />
        <div style={{ backgroundColor: 'white', padding: 16 }}>
          <Row gutter={[32, 32]}>
            <Col xs={24} md={12}>
              <Text type="secondary">Location (EN)</Text>
              <div>{getML(eventDetail?.locationML, 'en')}</div>
            </Col>
            <Col xs={24} md={12}>
              <Text type="secondary">Location (TH)</Text>
              <div>{getML(eventDetail?.locationML, 'th')}</div>
            </Col>
          </Row>
          {eventDetail?.locationUrl && (
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">Map URL</Text>
              <div>{eventDetail.locationUrl}</div>
            </div>
          )}
        </div>
      </div>

      {/* Organizer & Contact */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label="Organizer & Contact" />
        <div style={{ backgroundColor: 'white', padding: 16 }}>
          <Row gutter={[32, 32]}>
            <Col xs={24} md={12}>
              <Text type="secondary">Organizer (EN)</Text>
              <div>{getML(eventDetail?.organizerML, 'en')}</div>
            </Col>
            <Col xs={24} md={12}>
              <Text type="secondary">Organizer (TH)</Text>
              <div>{getML(eventDetail?.organizerML, 'th')}</div>
            </Col>
          </Row>
          {eventDetail?.registrationUrl && (
            <>
              <Divider style={{ marginTop: 16, marginBottom: 16 }} />
              <Text type="secondary">Registration URL</Text>
              <div>{eventDetail.registrationUrl}</div>
            </>
          )}
          {(eventDetail?.contactML?.en || eventDetail?.contactML?.th) && (
            <>
              <Divider style={{ marginTop: 16, marginBottom: 16 }} />
              <Row gutter={[32, 32]}>
                <Col xs={24} md={12}>
                  <Text type="secondary">Contact (EN)</Text>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{getML(eventDetail?.contactML, 'en')}</div>
                </Col>
                <Col xs={24} md={12}>
                  <Text type="secondary">Contact (TH)</Text>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{getML(eventDetail?.contactML, 'th')}</div>
                </Col>
              </Row>
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label="Statistics" />
        <div style={{ backgroundColor: 'white', padding: 16 }}>
          <div style={{ display: 'flex', gap: 64 }}>
            <div>
              <Text type="secondary">View Count</Text>
              <Title level={5} style={{ margin: 0, fontWeight: 'bold' }}>{eventDetail?.viewCount || 0}</Title>
            </div>
            <div>
              <Text type="secondary">Registrations</Text>
              <Title level={5} style={{ margin: 0, fontWeight: 'bold' }}>{regCount}</Title>
            </div>
            <div>
              <Text type="secondary">Max Participants</Text>
              <Title level={5} style={{ margin: 0, fontWeight: 'bold' }}>
                {eventDetail?.maxParticipants != null ? eventDetail.maxParticipants : 'Unlimited'}
              </Title>
            </div>
          </div>
          {eventDetail?.maxParticipants != null && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <Text type="secondary">
                  Capacity
                </Text>
                <Text strong style={{ color: regCount >= eventDetail.maxParticipants ? '#C62828' : undefined }}>
                  {`${regCount} / ${eventDetail.maxParticipants}`}
                  {regCount >= eventDetail.maxParticipants ? ' (Full)' : ` (${eventDetail.maxParticipants - regCount} remaining)`}
                </Text>
              </div>
              <Progress
                percent={Math.min(100, (regCount / eventDetail.maxParticipants) * 100)}
                showInfo={false}
                strokeColor={regCount >= eventDetail.maxParticipants ? '#C62828' : undefined}
              />
            </div>
          )}
        </div>
      </div>

      {/* Registrations */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label={`Registrations (${regCount})`} />
        <div style={{ backgroundColor: 'white' }}>
          {registrations.length > 0 ? (
            <>
              <div style={{ padding: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <AlumlyButton label="rxvWhite" icon={<DownloadIcon />} width={200} onClick={exportCsv}>
                  Export CSV
                </AlumlyButton>
              </div>
              <AlumlyDataGrid
                rows={registrations}
                columns={[
                  { field: 'firstName', headerName: 'First Name', width: 140 } as any,
                  { field: 'lastName', headerName: 'Last Name', width: 140 } as any,
                  { field: 'email', headerName: 'Email', flex: 1, width: 200 } as any,
                  { field: 'mobileNumber', headerName: 'Phone', width: 140 } as any,
                  // { field: 'faculty', headerName: 'Faculty', width: 160 } as any,
                  { field: 'graduationYear', headerName: 'SHI', width: 80 } as any,
                  // { field: 'studentId', headerName: 'Student ID', width: 120 } as any,
                  {
                    field: 'registeredAt',
                    headerName: 'Registered At',
                    width: 180,
                    valueGetter: (params: any) => DateTimeUtils.dateTimeFormat(params.row.registeredAt),
                  } as any,
                ] as any[]}
                autoHeight
                page={0}
                pageSize={10}
                rowCount={registrations.length}
              />
            </>
          ) : (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <Text type="secondary">No registrations yet</Text>
            </div>
          )}
        </div>
      </div>

      <Divider style={{ marginTop: 32, marginBottom: 32 }} />
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <AlumlyButton label="rxvWhite" width={128} onClick={() => navigate(RouteNameEnum.event.index)}>
          <span>Back</span>
        </AlumlyButton>
      </div>
    </div>
  );
};

export default EventDetail;
